/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from 'tracked-built-ins';
import AnalyticsService from 'cio/services/analytics';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import ApiService from 'cio/services/api';
import { isEmpty } from '@ember/utils';
import ReleaseNotificationService from 'cio/services/release-notification';

interface FakeDoorModalSignature {
  Element: HTMLElement;
  Args: {
    isShowingFakeDoorModal: boolean;
    onClose: () => unknown;
    onSubmit: ({ criticism }: { criticism: string }) => unknown;
  };
}

interface FakeDoorFormValues {
  walkthroughUnclear?: string;
  walkthroughImproved?: string;
  demoAutomationTools?: string;
  demoIndustry?: string;
  demoLookingFor?: string;
}

export default class FakeDoorForm extends Component<FakeDoorModalSignature> {
  @service analytics!: AnalyticsService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service api!: ApiService;
  @service releaseNotification!: ReleaseNotificationService;

  readonly modalViewOptions = [{ name: 'Workspace Walkthrough' }, { name: 'Live Demo Workspace' }];
  readonly videoOptions = { resumable: false };

  @tracked isShowingFakeDoorModal = this.args.isShowingFakeDoorModal;
  @tracked selectedOption = this.modalViewOptions[0].name;
  @tracked videoStarted = false;
  @tracked showVideoFollowUp = false;
  @tracked formValues: FakeDoorFormValues = {
    walkthroughUnclear: '',
    walkthroughImproved: '',
    demoAutomationTools: '',
    demoIndustry: '',
    demoLookingFor: '',
  };

  get hasFormValues() {
    return Object.values(this.formValues).some((field) => !isEmpty(field));
  }

  get formSubmitButtonIsDisabled() {
    return this.selectedOption === 'Live Demo Workspace' && !this.hasFormValues;
  }

  @action
  selectOption(name: string) {
    this.selectedOption = name;
    if (name === 'Live Demo Workspace') {
      this.analytics.track('onboarding', 'door_test_live_clicked', { account_id: this.account.current?.id });
    }
  }

  @action
  trackVideoStart() {
    this.analytics.track('onboarding', 'door_test_video_clicked', { account_id: this.account.current?.id });
    this.videoStarted = true;
  }

  @action
  setFormValue<T extends keyof FakeDoorFormValues>(key: T, value: FakeDoorFormValues[T]) {
    this.formValues = { ...this.formValues, [key]: value };
  }

  @action
  onSubmit(event: Event) {
    event.preventDefault();
    if (this.selectedOption === 'Workspace Walkthrough') {
      this.args.onClose();
      return;
    }
    if (this.hasFormValues) {
      let filledInForm = Object.entries(this.formValues)
        .filter(([, value]) => !isEmpty(value))
        .map(([key, value]) => `${key}: ${value}`);

      this.args.onSubmit({
        criticism: filledInForm.join('\r\n'),
      });
      return;
    }
  }
}
